import type { Identifier } from "ra-core";

/** A row of gula_wa_messages. */
export interface WaMessage {
  id: Identifier;
  contact_id: Identifier;
  direction: "in" | "out";
  origin: "client" | "crm" | "phone" | "auto";
  phone: string;
  type: string;
  body?: string | null;
  media_url?: string | null;
  media_type?: string | null;
  file_name?: string | null;
  status?: string | null;
  error?: string | null;
  sales_id?: Identifier | null;
  material_id?: Identifier | null;
  created_at: string;
}

/** A row of gula_wa_inbox. */
export interface WaThread {
  id: Identifier;
  contact_id: Identifier;
  phone?: string | null;
  last_message_at?: string | null;
  last_inbound_at?: string | null;
  last_preview?: string | null;
  last_direction?: string | null;
  unread: number;
  name?: string | null;
  client_id?: Identifier | null;
  stage?: string | null;
  sales_id?: Identifier | null;
  lead_temperature?: string | null;
  city?: string | null;
  window_open?: boolean | null;
}

export type InboxFilter = "all" | "unread" | "mine";

const WINDOW_MS = 24 * 60 * 60 * 1000;

/** Meta only allows free text within 24 h of the client's last message. */
export const isWindowOpen = (lastInboundAt?: string | null, now = Date.now()) =>
  !!lastInboundAt && now - new Date(lastInboundAt).getTime() < WINDOW_MS;

/** "quedan 5 h" until the 24 h window closes. */
export const windowLeft = (lastInboundAt?: string | null, now = Date.now()) => {
  if (!isWindowOpen(lastInboundAt, now)) return null;
  const hours = Math.ceil(
    (new Date(lastInboundAt as string).getTime() + WINDOW_MS - now) / 3600000,
  );
  return hours <= 1 ? "queda menos de 1 h" : `quedan ${hours} h`;
};

interface FilterableThread {
  unread: number;
  sales_id?: Identifier | null;
  name?: string | null;
  phone?: string | null;
  /** Phone or @username in the unified inbox. */
  handle?: string | null;
  last_message_at?: string | null;
}

export const filterThreads = <T extends FilterableThread>(
  threads: T[],
  filter: InboxFilter,
  query: string,
  meId?: Identifier | null,
): T[] => {
  const needle = query.trim().toLowerCase();
  const digits = query.replace(/\D/g, "");
  return threads
    .filter((thread) =>
      filter === "unread"
        ? thread.unread > 0
        : filter === "mine"
          ? String(thread.sales_id) === String(meId)
          : true,
    )
    .filter((thread) => {
      if (!needle) return true;
      const handle = thread.phone ?? thread.handle ?? "";
      return (
        (thread.name ?? "").toLowerCase().includes(needle) ||
        handle.toLowerCase().includes(needle) ||
        (digits.length >= 3 && handle.replace(/\D/g, "").includes(digits))
      );
    })
    .sort((a, b) =>
      (b.last_message_at ?? "").localeCompare(a.last_message_at ?? ""),
    );
};

/** "10:42", "ayer", "lun 19". */
export const shortTime = (iso?: string | null, now = new Date()) => {
  if (!iso) return "";
  const date = new Date(iso);
  const days = Math.floor(
    (new Date(now.toDateString()).getTime() -
      new Date(date.toDateString()).getTime()) /
      86400000,
  );
  if (days === 0)
    return date.toLocaleTimeString("es-ES", {
      hour: "2-digit",
      minute: "2-digit",
    });
  if (days === 1) return "ayer";
  return date.toLocaleDateString("es-ES", { weekday: "short", day: "numeric" });
};

/** wa.me link to keep writing from the phone when the 24 h window is closed. */
export const phoneChatUrl = (phone: string, text?: string) => {
  let digits = phone.replace(/\D/g, "");
  if (digits.length === 9) digits = `34${digits}`;
  return `https://wa.me/${digits}${text ? `?text=${encodeURIComponent(text)}` : ""}`;
};

/** Last 9 digits of a phone (Spanish number without the prefix), or "". */
export const phone9 = (phone?: string | null): string =>
  (phone ?? "").replace(/\D/g, "").slice(-9);
